import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, BehaviorSubject, Subscription, switchMap, skip, take, mergeMap, filter, of, forkJoin } from 'rxjs';
import { DiaryInstanceInterface } from 'src/app/models/interface/diary-instance.interface';
import { DiaryInterface } from 'src/app/models/interface/diary.interface';
import { LessonInterface } from 'src/app/models/interface/lesson.interface';
import { StudyInterface } from 'src/app/models/interface/study/study.interface';
import { UserInterface } from 'src/app/models/interface/user.interface';
import { HelperService } from 'src/app/services/helper/helper.service';
import { GroupSharedService } from 'src/app/services/shared/group-shared/group-shared.service';
import { DiaryStore } from 'src/app/store/diary/component-store/diary.store';
import { DiaryActionTypes } from 'src/app/store/diary/diary.action';
import { StudyStore } from 'src/app/store/study/component-store/study.store';
import { UserActionTypes } from 'src/app/store/user/user.action';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { GetAllDiariesPayload } from 'src/app/store/diary/diary.effects';

type InstanceDiaryPair = {
  diary_instance: DiaryInstanceInterface;
  diary: DiaryInterface;
};

@Component({
  selector: 'app-group-diary',
  templateUrl: './group-diary.component.html',
  styleUrls: ['./group-diary.component.scss'],
  providers: [DiaryStore, StudyStore],
})
export class GroupDiaryComponent implements OnDestroy, OnInit {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Study ID and loading status for this child component
  public studyId: number;
  public isLoading$: Observable<boolean>;

  public showOnlyOwnDiaryInstances = false;

  isManager = false;

  public questionnaires: LessonInterface[] = [];

  public diaryInstances: DiaryInstanceInterface[] = [];
  public diaryInstances$: Observable<DiaryInstanceInterface[]>;
  public diaries: DiaryInterface[] = [];
  public diaries$: Observable<DiaryInterface[]>;

  public pagedInstanceDiaryPair$: Observable<InstanceDiaryPair[]>;
  public instanceDiaryPairs$: Observable<InstanceDiaryPair[]>;

  public studyMembers: UserInterface[] = [];

  private myMembers$: Observable<UserInterface[]>;
  private studyMembers$: Observable<UserInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private instanceDiaryPair: InstanceDiaryPair[] = [];

  // Diary instance & diary
  private instanceDiaryPairsSubject = new BehaviorSubject<InstanceDiaryPair[]>([]);

  private pagedInstanceDiaryPairSubject = new BehaviorSubject<InstanceDiaryPair[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: GroupSharedService,
    private store: Store<{ diaries: DiaryInterface[]; myMembers: UserInterface[] }>,
    private diaryStore: DiaryStore,
    private studyStore: StudyStore,
    private helperService: HelperService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.instanceDiaryPairs$ = this.instanceDiaryPairsSubject.asObservable();
    this.pagedInstanceDiaryPair$ = this.pagedInstanceDiaryPairSubject.asObservable();
    this.diaries$ = this.store.select('diaries');
    this.diaryInstances$ = this.diaryStore.allDiaryInstances$;
    this.studyMembers$ = this.studyStore.studyMembers$;
    this.myMembers$ = this.store.select('myMembers');
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData() {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.sharedService.isManager$
        .pipe(
          switchMap((isManager) => {
            this.isManager = isManager;
            return this.sharedService.sourceGroup$.pipe(
              filter((study: StudyInterface) => study !== null),
              switchMap((study: StudyInterface) => {
                this.studyId = study.id;
                const diaryInstances = this.loadDiaryInstances(this.studyId, isManager);
                const diaries = this.loadDiaries(this.studyId);
                const members = this.loadMembers(this.studyId, isManager);
                return forkJoin({ diaryInstances, diaries, members });
              }),
            );
          }),
        )
        .subscribe((results) => {
          this.diaries = results.diaries;
          this.diaryInstances = results.diaryInstances.filter(
            (diaryInstance) => diaryInstance.attributes?.study_id?.toString() === this.studyId.toString(),
          );
          this.instanceDiaryPair = [];
          this.diaryInstances.forEach((diaryInstance) => {
            const parentDiary = this.diaries.find(
              (diary) => diary.id.toString() === diaryInstance.attributes.diary_id.toString(),
            );
            this.instanceDiaryPair.push({ diary_instance: diaryInstance, diary: parentDiary });
          });
          this.instanceDiaryPairsSubject.next(this.instanceDiaryPair);
          const currentPageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
          this.pagedInstanceDiaryPairSubject.next(this.instanceDiaryPairsSubject.value.slice(0, currentPageSize));
          this.isLoadingSubject.next(false);
        }),
    );
  }

  private loadDiaryInstances(studyId: number, isManager: boolean) {
    if (isManager && !this.showOnlyOwnDiaryInstances) {
      this.diaryStore.getAllDiaryInstancesOfStudy({ studyId });
    } else {
      this.diaryStore.getAllInstancesOfDiaryECoach({});
    }
    return this.diaryInstances$.pipe(skip(1), take(1));
  }

  private loadDiaries(studyId: number) {
    const payload: GetAllDiariesPayload = { studyId };
    this.store.dispatch({ type: DiaryActionTypes.getAllDiariesType, payload });
    return this.diaries$.pipe(skip(1), take(1));
  }

  private loadMembers(studyId: number, isManager: boolean): Observable<boolean> {
    if (isManager) {
      this.studyStore.getMembers({ studyId });
      return this.studyMembers$.pipe(
        skip(1),
        take(1),
        mergeMap((result) => {
          this.studyMembers = result;
          return of(true);
        }),
      );
    } else {
      this.store.dispatch({ type: UserActionTypes.getMyMembersType, payload: {} });
      const myMembers = this.myMembers$.pipe(skip(1), take(1));
      this.studyStore.getECoachMembers({ studyId });
      const studyMembers = this.studyMembers$.pipe(skip(1), take(1));
      return forkJoin({ myMembers, studyMembers }).pipe(
        mergeMap(
          (results) => {
            this.studyMembers = results.studyMembers.filter(
              (studyMember) => results.myMembers.some((member) => member.id === studyMember.id),
            );
            return of(true);
          },
        ),
      );
    }
  }

  trackByDiaryInstance(_index: number, element: InstanceDiaryPair): number {
    return element.diary_instance.id;
  }

  updatePagedDiaryInstances(event: InstanceDiaryPair[]) {
    if (event) {
      this.pagedInstanceDiaryPairSubject.next(event);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
